import type { ReactNode } from 'react'
import { Button, Card, Icon, Stack, Text } from '@/design-system'
import { Logo } from '@/app/Logo'
import styles from './FlowSuccess.module.css'

export interface SuccessRow {
  readonly label: string
  readonly value: string
  readonly emphasis?: boolean
}

export interface FlowSuccessProps {
  readonly headline: string
  readonly amount: string
  readonly caption: string
  readonly rows: readonly SuccessRow[]
  readonly primary: { readonly label: string; readonly to: string }
  readonly secondary?: { readonly label: string; readonly to: string }
  readonly footnote?: ReactNode
}

/**
 * Every money flow ends here. One shape for all of them, so the moment after
 * you commit money always looks and reads the same.
 */
export function FlowSuccess({
  headline,
  amount,
  caption,
  rows,
  primary,
  secondary,
  footnote,
}: FlowSuccessProps) {
  return (
    <div className={styles.shell}>
      <Stack className={styles.card} gap={5} align="stretch">
        <Stack align="center" gap={5}>
          <Logo size={22} />
          <span className={styles.seal}>
            <Icon name="check" size={28} />
          </span>
          <Stack align="center" gap={2}>
            <Text as="h1" variant="title" weight="bold">
              {headline}
            </Text>
            <Text as="p" variant="display" weight="bold" numeric>
              {amount}
            </Text>
            <Text variant="caption" tone="secondary">
              {caption}
            </Text>
          </Stack>
        </Stack>

        <Card tone="light">
          <div className={styles.rows}>
            {rows.map((row) => (
              <div key={row.label} className={styles.row}>
                <Text variant="caption" tone="secondary">
                  {row.label}
                </Text>
                <Text
                  variant="caption"
                  weight="semibold"
                  numeric
                  tone={row.emphasis ? 'positive' : 'primary'}
                >
                  {row.value}
                </Text>
              </div>
            ))}
          </div>
        </Card>

        <Stack gap={3}>
          <Button size="lg" block iconEnd="arrow_forward" to={primary.to}>
            {primary.label}
          </Button>
          {secondary && (
            <Button variant="ghost" size="md" block to={secondary.to}>
              {secondary.label}
            </Button>
          )}
        </Stack>

        {footnote && (
          <Text variant="caption" tone="secondary" style={{ textAlign: 'center' }}>
            {footnote}
          </Text>
        )}
      </Stack>
    </div>
  )
}
